var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import ActionButton from '@rad/components/ui/action-button';
import Modal from '@rad/components/ui/modal';
import Input from '@rad/components/inputs/input';
import createTable from '@rad/components/ui/table';
import FileUpload from '../../_shared/components/file-upload';
import { useFetch } from '@rad/hooks';
import { expand } from '@rad/utility/validation';
import { getFaqs, createFaq, updateFaq, deleteFaq } from '../../../common/network/faq';
import { getForms, createForm, updateForm, deleteForm } from '../../../common/network/form';
import { uploadFile } from '../../../common/network/attachment';
import { useToast } from '../../../contexts/toast';
import { useAuth } from '../../../hooks/useAuth';
import { Permission } from '../../../common/routes/types';
import { lineClamp } from '../../../common/styles';
const emptyFaqForm = () => ({
    id: null,
    question: '',
    answer: '',
    sortOrder: 0,
});
const emptyFormData = () => ({
    id: null,
    name: '',
    description: '',
    fileUrl: '',
    fileAttachmentId: null,
    sortOrder: 0,
});
const AdminContentResources = () => {
    const toast = useToast();
    const [auth] = useAuth();
    // Form writes take homepage-*, as the FAQ writes beside them do (src/server/routes/forms.fs).
    const canCreateForm = auth.hasPermission([Permission.HomepageCreate]);
    const canUpdateForm = auth.hasPermission([Permission.HomepageUpdate]);
    const canDeleteForm = auth.hasPermission([Permission.HomepageDelete]);
    const [showFaqModal, setShowFaqModal] = React.useState(false);
    const [showFormModal, setShowFormModal] = React.useState(false);
    const [faqForm, setFaqForm] = React.useState(emptyFaqForm());
    const [formData, setFormData] = React.useState(emptyFormData());
    const [faqErrors, setFaqErrors] = React.useState({});
    const [formErrors, setFormErrors] = React.useState({});
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [faqsPromise, refetchFaqs] = useFetch(getFaqs());
    const [formsPromise, refetchForms] = useFetch(getForms());
    const FaqTable = React.useMemo(() => createTable(faqsPromise), [faqsPromise]);
    const FormTable = React.useMemo(() => createTable(formsPromise), [formsPromise]);
    const openAddFaq = () => {
        setFaqForm(emptyFaqForm());
        setFaqErrors({});
        setShowFaqModal(true);
    };
    const openEditFaq = (faq) => {
        setFaqForm(Object.assign({}, faq));
        setFaqErrors({});
        setShowFaqModal(true);
    };
    const handleSaveFaq = () => __awaiter(void 0, void 0, void 0, function* () {
        setFaqErrors({});
        const [success, errors] = faqForm.id ? yield updateFaq(faqForm) : yield createFaq(faqForm);
        if (success) {
            setShowFaqModal(false);
            refetchFaqs();
            toast.success(faqForm.id ? 'FAQ updated' : 'FAQ created');
        }
        else {
            setFaqErrors(expand(errors)[1]);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteFaq = (faq) => __awaiter(void 0, void 0, void 0, function* () {
        if (confirm(`Are you sure you want to delete this FAQ?`)) {
            yield deleteFaq(faq.id);
            refetchFaqs();
            toast.success('FAQ deleted');
        }
    });
    const openAddForm = () => {
        setFormData(emptyFormData());
        setFormErrors({});
        setSelectedFile(null);
        setShowFormModal(true);
    };
    const openEditForm = (form) => {
        setFormData(Object.assign({}, form));
        setFormErrors({});
        setSelectedFile(null);
        setShowFormModal(true);
    };
    const handleSaveForm = () => __awaiter(void 0, void 0, void 0, function* () {
        setFormErrors({});
        let finalData = Object.assign({}, formData);
        if (selectedFile) {
            const result = yield uploadFile(selectedFile, 'form');
            if (result) {
                finalData = Object.assign(Object.assign({}, finalData), { fileUrl: result.url, fileAttachmentId: result.id });
            }
            else {
                setFormErrors({ fileUrl: ['File upload failed. Please try again.'] });
                return;
            }
        }
        if (!finalData.fileUrl) {
            setFormErrors({ fileUrl: ['A file is required.'] });
            return;
        }
        const [success, errors] = finalData.id ? yield updateForm(finalData) : yield createForm(finalData);
        if (success) {
            setShowFormModal(false);
            setSelectedFile(null);
            refetchForms();
            toast.success(finalData.id ? 'Form updated' : 'Form created');
        }
        else {
            setFormErrors(expand(errors)[1]);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteForm = (form) => __awaiter(void 0, void 0, void 0, function* () {
        if (confirm(`Are you sure you want to delete "${form.name}"?`)) {
            yield deleteForm(form.id);
            refetchForms();
            toast.success('Form deleted');
        }
    });
    const closeFaqModal = () => {
        setShowFaqModal(false);
        setFaqErrors({});
    };
    const closeFormModal = () => {
        setShowFormModal(false);
        setFormErrors({});
        setSelectedFile(null);
    };
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Resources" }), _jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("div", { className: "fw-semibold", children: "Forms" }), canCreateForm && (_jsx("button", { className: "btn btn-outline-secondary btn-sm", onClick: openAddForm, children: "Add New" }))] }), _jsxs(FormTable, { className: "mb-0", children: [_jsx(FormTable.Column, { header: "Name", accessor: "name", className: "fw-medium" }), _jsx(FormTable.Column, { header: "Description", accessor: "description", className: "text-muted small" }), _jsx(FormTable.Column, { header: "File", render: form => form.fileUrl ? (_jsxs("a", { href: form.fileUrl, target: "_blank", rel: "noopener noreferrer", className: "text-primary small", children: [_jsx("i", { className: "bi bi-file-earmark me-1" }), "View File"] })) : (_jsx("span", { className: "text-muted small", children: "No file" })) }), _jsx(FormTable.Column, { header: "", className: "text-end", render: form => (_jsxs(_Fragment, { children: [canUpdateForm && (_jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: () => openEditForm(form), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) })), canDeleteForm && (_jsx(ActionButton, { className: "btn btn-link text-danger p-0", onClick: () => handleDeleteForm(form), title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) }))] })) })] })] }), _jsxs("div", { className: "mb-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsx("div", { className: "fw-semibold", children: "FAQs" }), _jsx("button", { className: "btn btn-outline-secondary btn-sm", onClick: openAddFaq, children: "Add New" })] }), _jsxs(FaqTable, { className: "mb-0", children: [_jsx(FaqTable.Column, { header: "Question", accessor: "question", className: "fw-medium" }), _jsx(FaqTable.Column, { header: "Answer", render: faq => (_jsx("div", { className: "text-muted small", style: Object.assign({ maxWidth: 400 }, lineClamp(2)), children: faq.answer })) }), _jsx(FaqTable.Column, { header: "", className: "text-end", render: faq => (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: () => openEditFaq(faq), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) }), _jsx(ActionButton, { className: "btn btn-link text-danger p-0", onClick: () => handleDeleteFaq(faq), title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) })] })) })] })] }), _jsx(Modal, { isOpen: showFaqModal, title: faqForm.id ? 'Edit FAQ' : 'Add FAQ', onClose: closeFaqModal, onSave: handleSaveFaq, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(Input, { label: "Question", kind: "textarea", value: faqForm.question, onChange: val => setFaqForm(Object.assign(Object.assign({}, faqForm), { question: val })), invalid: faqErrors.question }), _jsx(Input, { label: "Answer", kind: "textarea", value: faqForm.answer, onChange: val => setFaqForm(Object.assign(Object.assign({}, faqForm), { answer: val })), invalid: faqErrors.answer })] }) }), _jsx(Modal, { isOpen: showFormModal, title: formData.id ? 'Edit Form' : 'Add Form', onClose: closeFormModal, onSave: handleSaveForm, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(Input, { label: "Name", kind: "text", value: formData.name, onChange: val => setFormData(Object.assign(Object.assign({}, formData), { name: val })), invalid: formErrors.name }), _jsx(Input, { label: "Description", kind: "textarea", value: formData.description, onChange: val => setFormData(Object.assign(Object.assign({}, formData), { description: val })) }), _jsx(FileUpload, { label: "File", accept: ".pdf,.doc,.docx,.xls,.xlsx", file: selectedFile, onChange: setSelectedFile, currentUrl: formData.id ? formData.fileUrl : null, invalid: formErrors.fileUrl })] }) })] }) }));
};
export default AdminContentResources;
